import { Alert } from '@/components/ui/alert';
import { DatePicker } from '@/components/ui/date-picker/DatePicker';
import { RadioCardItem, RadioCardRoot } from '@/components/ui/radio-card';
import { useUpdatePayoutScheduleAction } from '@/hooks/actions/use-update-payout-schedule-action';
import type { PayoutScheduleOption } from '@/stores/payout-checkout-store';
import { usePayoutCheckoutStore } from '@/stores/payout-checkout-store';
import { Box, Field, HStack } from '@chakra-ui/react';
import type { CalendarDate, DateValue } from '@internationalized/date';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { format } from 'date-fns';
import * as React from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// Define form values interface
interface SchedulePayoutFormValues {
  payoutScheduleOption: PayoutScheduleOption;
  scheduledPayoutDate: Date | null; // RHF stores standard Date
}

export const SchedulePayoutSection: React.FC = () => {
  const { t } = useTranslation();
  const initialOption = usePayoutCheckoutStore((state) => state.payoutScheduleOption);
  const initialDateString = usePayoutCheckoutStore((state) => state.scheduledPayoutDate);
  const payoutId = usePayoutCheckoutStore((state) => state.payout?.id);
  const setPayoutScheduleOption = usePayoutCheckoutStore((state) => state.setPayoutScheduleOption);
  const setStoreScheduledPayoutDate = usePayoutCheckoutStore(
    (state) => state.setScheduledPayoutDate
  );

  const updateScheduleMutation = useUpdatePayoutScheduleAction();

  // Parse the initial date string from the store into a Date object for RHF
  const initialDateForRHF = React.useMemo(() => {
    if (!initialDateString) return null;
    try {
      return parseDate(initialDateString.split('T')[0]).toDate(getLocalTimeZone()); // Ensure we only parse YYYY-MM-DD for parseDate
    } catch (e) {
      console.error('Failed to parse initial date string for RHF:', initialDateString, e);
      return null;
    }
  }, [initialDateString]);

  const { control, setValue } = useForm<SchedulePayoutFormValues>({
    values: {
      payoutScheduleOption: initialOption,
      scheduledPayoutDate: initialDateForRHF, // Use parsed date
    },
  });

  const watchedOption = useWatch({ control, name: 'payoutScheduleOption' });
  const watchedDate = useWatch({ control, name: 'scheduledPayoutDate' });

  const formattedDate = watchedDate
    ? format(watchedDate, 'MMM d, yyyy')
    : t('payouts.checkout.schedule.selectedDate');

  const paymentTimingText =
    watchedOption === 'asap'
      ? t('payouts.checkout.schedule.immediately')
      : t('payouts.checkout.schedule.onDate', { date: formattedDate });

  // Handler for schedule option change
  const handleOptionChange = (newOption: PayoutScheduleOption) => {
    if (!payoutId) return;
    let newDate = watchedDate;
    if (newOption === 'scheduled' && !watchedDate) {
      const tomorrowDate = today(getLocalTimeZone()).add({ days: 1 });
      newDate = fromDateValue(tomorrowDate);
      setValue('scheduledPayoutDate', newDate, { shouldDirty: true });
    } else if (newOption === 'asap') {
      newDate = null;
      setValue('scheduledPayoutDate', null, { shouldDirty: true });
    }
    updateScheduleMutation.mutate(
      {
        payoutId,
        scheduledPayoutDate: toDateString(newDate),
      },
      {
        onSuccess: (data) => {
          setPayoutScheduleOption(newOption);
          setStoreScheduledPayoutDate(
            data.payout.scheduledPayoutAt ? data.payout.scheduledPayoutAt : null
          );
        },
      }
    );
  };

  // Handler for date change
  const handleDateChange = (newDate: Date | null) => {
    if (!payoutId) return;
    updateScheduleMutation.mutate(
      {
        payoutId,
        scheduledPayoutDate: toDateString(newDate),
      },
      {
        onSuccess: (data) => {
          // Use the date from API response to avoid timezone issues
          // The backend returns the correctly stored date
          setStoreScheduledPayoutDate(data.payout.scheduledPayoutAt ?? null);
        },
      }
    );
  };

  return (
    <>
      <Field.Root mt={6}>
        <Field.Label fontSize="lg" fontWeight="semibold" mb={3}>
          {t('payouts.checkout.schedule.title')}
        </Field.Label>
        <Controller
          name="payoutScheduleOption"
          control={control}
          render={({ field }) => (
            <RadioCardRoot
              colorPalette="primary"
              value={field.value}
              onValueChange={(details) => {
                const newOption = (details.value ?? 'asap') as PayoutScheduleOption;
                field.onChange(newOption);
                handleOptionChange(newOption);
              }}
            >
              <HStack gap={4} align="stretch">
                <RadioCardItem
                  value="asap"
                  flex={1}
                  rounded="xl"
                  label={t('payouts.checkout.schedule.asap')}
                  description={t('payouts.checkout.schedule.asapDescription')}
                />

                <RadioCardItem
                  value="scheduled"
                  flex={1}
                  rounded="xl"
                  label={t('payouts.checkout.schedule.scheduled')}
                  description={t('payouts.checkout.schedule.scheduledDescription')}
                />
              </HStack>
            </RadioCardRoot>
          )}
        />
      </Field.Root>

      {watchedOption === 'scheduled' && (
        <Box mt={4}>
          <Controller
            name="scheduledPayoutDate"
            control={control}
            rules={{ required: watchedOption === 'scheduled' ? 'Date is required' : false }}
            render={({ field, fieldState }) => (
              <DatePicker
                value={toCalendarDate(field.value)}
                onChange={(dateValue) => {
                  const newDate = fromDateValue(dateValue);
                  field.onChange(newDate);
                  handleDateChange(newDate);
                }}
                minValue={today(getLocalTimeZone()).add({ days: 1 })}
                isInvalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </Box>
      )}

      <Alert
        status="info"
        title={t('payouts.checkout.schedule.paymentProcessed', { when: paymentTimingText })}
        variant="surface"
        rounded="xl"
        colorPalette="primary"
        mt={4}
      >
        {t('payouts.checkout.schedule.processingInfo')}
      </Alert>
    </>
  );
};

// Helper to convert JS Date to CalendarDate
const toCalendarDate = (date: Date | null): CalendarDate | null => {
  if (!date) return null;
  // Use local date components to avoid timezone shift when converting to ISO string
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return parseDate(`${year}-${month}-${day}`);
};

// Helper to convert DateValue (CalendarDate) back to JS Date
const fromDateValue = (dateValue: DateValue | null): Date | null => {
  if (!dateValue) return null;
  return dateValue.toDate(getLocalTimeZone());
};

// Helper to convert Date to YYYY-MM-DD string (avoids timezone issues)
const toDateString = (date: Date | null): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
